import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Pencil, Trash2, ImagePlus, X, CalendarPlus } from "lucide-react";
import { format } from "date-fns";

interface MyEvent {
  id: string;
  title: string;
  type: string;
  date: string;
  start_time: string;
  end_time: string;
  location: string;
  description: string;
  external_link: string | null;
  is_special: boolean;
  chapter: "washington" | "union" | "all";
  image_url: string | null;
  created_by: string | null;
}

interface MyCreatedEventsProps {
  userId: string;
}

const parseLocalDate = (s: string): Date => {
  const [y, m, d] = s.split("-").map(Number);
  return new Date(y, m - 1, d);
};

const MyCreatedEvents = ({ userId }: MyCreatedEventsProps) => {
  const [events, setEvents] = useState<MyEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingEvent, setEditingEvent] = useState<MyEvent | null>(null);
  const [saving, setSaving] = useState(false);
  const [flyerFile, setFlyerFile] = useState<File | null>(null);
  const [flyerPreview, setFlyerPreview] = useState<string | null>(null);
  const [existingFlyerUrl, setExistingFlyerUrl] = useState<string | null>(null);
  const [removeFlyer, setRemoveFlyer] = useState(false);
  const [formData, setFormData] = useState({
    title: "",
    date: "",
    start_time: "",
    end_time: "",
    location: "",
    description: "",
    external_link: "",
    chapter: "all" as "washington" | "union" | "all",
  });

  const fetchEvents = async () => {
    if (!userId) return;
    setLoading(true);
    const { data, error } = await supabase
      .from("events")
      .select("*")
      .eq("created_by", userId)
      .order("date", { ascending: false });
    if (error) {
      toast.error("Error loading your events");
    } else {
      setEvents((data as MyEvent[]) || []);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchEvents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  const handleEdit = (event: MyEvent) => {
    setEditingEvent(event);
    setFlyerFile(null);
    setFlyerPreview(event.image_url || null);
    setExistingFlyerUrl(event.image_url || null);
    setRemoveFlyer(false);
    setFormData({
      title: event.title,
      date: event.date,
      start_time: event.start_time,
      end_time: event.end_time,
      location: event.location,
      description: event.description,
      external_link: event.external_link || "",
      chapter: event.chapter,
    });
  };

  const handleFlyerChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Please select an image file");
      return;
    }
    setFlyerFile(file);
    setRemoveFlyer(false);
    const reader = new FileReader();
    reader.onload = (ev) => setFlyerPreview(ev.target?.result as string);
    reader.readAsDataURL(file);
  };

  const clearFlyer = () => {
    setFlyerFile(null);
    setFlyerPreview(null);
    setRemoveFlyer(true);
  };

  const extractStoragePath = (url: string): string | null => {
    const marker = "/event-flyers/";
    const idx = url.indexOf(marker);
    if (idx === -1) return null;
    return url.substring(idx + marker.length);
  };

  const uploadFlyer = async (): Promise<string | null> => {
    if (!flyerFile) {
      return removeFlyer ? null : existingFlyerUrl;
    }
    const fileExt = flyerFile.name.split(".").pop();
    const filePath = `${userId}/${Date.now()}.${fileExt}`;
    const { error } = await supabase.storage
      .from("event-flyers")
      .upload(filePath, flyerFile);
    if (error) throw error;
    const { data: urlData } = supabase.storage
      .from("event-flyers")
      .getPublicUrl(filePath);
    return urlData.publicUrl;
  };

  const handleSave = async () => {
    if (!editingEvent) return;
    setSaving(true);
    try {
      const newImageUrl = await uploadFlyer();

      if (
        existingFlyerUrl &&
        existingFlyerUrl !== newImageUrl &&
        existingFlyerUrl.includes(`/event-flyers/${userId}/`)
      ) {
        const path = extractStoragePath(existingFlyerUrl);
        if (path) {
          await supabase.storage.from("event-flyers").remove([path]);
        }
      }

      const { error } = await supabase
        .from("events")
        .update({
          title: formData.title,
          date: formData.date,
          start_time: formData.start_time,
          end_time: formData.end_time,
          location: formData.location,
          description: formData.description,
          external_link: formData.external_link || null,
          chapter: formData.chapter,
          image_url: newImageUrl,
        })
        .eq("id", editingEvent.id);

      if (error) throw error;
      toast.success("Event updated");
      setEditingEvent(null);
      fetchEvents();
    } catch (error: any) {
      toast.error("Error saving event: " + error.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (event: MyEvent) => {
    if (!confirm(`Delete "${event.title}"? This cannot be undone.`)) return;

    if (event.image_url && event.image_url.includes(`/event-flyers/${userId}/`)) {
      const path = extractStoragePath(event.image_url);
      if (path) {
        await supabase.storage.from("event-flyers").remove([path]);
      }
    }

    const { error } = await supabase.from("events").delete().eq("id", event.id);
    if (error) {
      toast.error("Error deleting event: " + error.message);
      return;
    }
    toast.success("Event deleted");
    fetchEvents();
  };

  const handleChange = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <CalendarPlus className="h-5 w-5 text-primary" />
          Events I Created
        </CardTitle>
        <CardDescription>
          Manage events you've added to the RCN calendar.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {loading ? (
          <p className="text-center text-muted-foreground py-8">Loading...</p>
        ) : events.length === 0 ? (
          <p className="text-center text-muted-foreground py-8">
            You haven't created any events yet. Use the "Create Event" tab to add one.
          </p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Title</TableHead>
                <TableHead>Date</TableHead>
                <TableHead className="hidden md:table-cell">Location</TableHead>
                <TableHead>Chapter</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {events.map((event) => (
                <TableRow key={event.id}>
                  <TableCell className="font-medium">
                    <div className="flex items-center gap-2">
                      {event.image_url && (
                        <img
                          src={event.image_url}
                          alt=""
                          className="h-10 w-10 object-cover rounded border hidden sm:block"
                        />
                      )}
                      <span>{event.title}</span>
                    </div>
                  </TableCell>
                  <TableCell>
                    {format(parseLocalDate(event.date), "MMM d, yyyy")}
                  </TableCell>
                  <TableCell className="hidden md:table-cell">{event.location}</TableCell>
                  <TableCell>
                    <Badge variant="outline" className="capitalize">
                      {event.chapter}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right space-x-1">
                    <Button variant="ghost" size="icon" onClick={() => handleEdit(event)}>
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button variant="ghost" size="icon" onClick={() => handleDelete(event)}>
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>

      <Dialog
        open={!!editingEvent}
        onOpenChange={(open) => {
          if (!open) setEditingEvent(null);
        }}
      >
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Edit: {editingEvent?.title}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            <div className="space-y-2">
              <Label>Event Title *</Label>
              <Input
                value={formData.title}
                onChange={(e) => handleChange("title", e.target.value)}
                required
              />
            </div>
            <div className="space-y-2">
              <Label>Chapter *</Label>
              <Select
                value={formData.chapter}
                onValueChange={(v) => handleChange("chapter", v)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Chapters (Shared Event)</SelectItem>
                  <SelectItem value="washington">Washington Chapter Only</SelectItem>
                  <SelectItem value="union">Union Chapter Only</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Date *</Label>
                <Input
                  type="date"
                  value={formData.date}
                  onChange={(e) => handleChange("date", e.target.value)}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label>Start Time *</Label>
                <Input
                  type="time"
                  value={formData.start_time}
                  onChange={(e) => handleChange("start_time", e.target.value)}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label>End Time *</Label>
                <Input
                  type="time"
                  value={formData.end_time}
                  onChange={(e) => handleChange("end_time", e.target.value)}
                  required
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Location *</Label>
              <Input
                value={formData.location}
                onChange={(e) => handleChange("location", e.target.value)}
                required
              />
            </div>
            <div className="space-y-2">
              <Label>Description *</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => handleChange("description", e.target.value)}
                rows={4}
                required
              />
            </div>
            <div className="space-y-2">
              <Label>Event Flyer (Optional)</Label>
              {flyerPreview ? (
                <div className="relative inline-block">
                  <img
                    src={flyerPreview}
                    alt="Flyer preview"
                    className="max-h-64 rounded-lg border object-contain"
                  />
                  <Button
                    type="button"
                    variant="destructive"
                    size="icon"
                    className="absolute top-2 right-2 h-7 w-7"
                    onClick={clearFlyer}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              ) : (
                <label
                  htmlFor="my-event-flyer-upload"
                  className="flex flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed border-muted-foreground/25 p-8 cursor-pointer hover:border-primary/50 transition-colors"
                >
                  <ImagePlus className="h-8 w-8 text-muted-foreground" />
                  <span className="text-sm text-muted-foreground">
                    Click to upload a flyer image
                  </span>
                  <input
                    id="my-event-flyer-upload"
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={handleFlyerChange}
                  />
                </label>
              )}
            </div>
            <div className="space-y-2">
              <Label>External Link (Optional)</Label>
              <Input
                type="url"
                value={formData.external_link}
                onChange={(e) => handleChange("external_link", e.target.value)}
              />
            </div>
            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={() => setEditingEvent(null)}>
                Cancel
              </Button>
              <Button onClick={handleSave} disabled={saving}>
                {saving ? "Saving..." : "Save Changes"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default MyCreatedEvents;
